import { type Dispatch, type ReactNode, type SetStateAction, useRef, useState } from 'react';
import {
  Anchor,
  Box,
  Button,
  Form,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Text,
  Tooltip,
} from '@zillow/constellation-10';
import type {
  RenterInfoRenterProfileStandalonePageWebApiResponse,
  RenterProfileHasPets,
  RenterProfileStandalonePagePreferredBedroomCount,
  RenterProfileStandalonePagePreferredLeaseDuration,
  RenterProfileStandalonePageTotalPeopleInHousehold,
} from '@zg-rentals/ts-renter-info-client';
import {
  ASSISTANCE_ANIMAL_DISCLAIMER_TEXT,
  ChipSelectField,
  CreditScoreField,
  emitRenterProfileClearEvent,
  emitRenterProfileNoCreditScoreEvent,
  emitRenterProfileSubmitEvent,
  formatPhoneNumberInput,
  HousingVoucherField,
  HUD_ANIMAL_POLICY_URL,
  IncomeInputField,
  LEASE_DURATION_OPTIONS,
  localDateToString,
  MONTHS_PER_YEAR,
  MoveInDateField,
  NUM_BEDROOMS_OPTIONS,
  NUM_HOUSEHOLD_OPTIONS,
  PARKING_OPTIONS,
  PET_OPTIONS,
  stringToLocalDate,
  validateMoveInDate,
  validateName,
  validatePhone,
} from '@zg-rentals/renter-profile-components';
import { RENTER_PROFILE_ROOT_ID } from '../../../../constants';
import type { MapCustomErrorFromRequest } from '../../RenterProfilePage';

type Profile = RenterInfoRenterProfileStandalonePageWebApiResponse;

type EditRenterProfileContentProps = {
  profile: Profile;
  setCustomErrorMap?: Dispatch<SetStateAction<MapCustomErrorFromRequest>>;
  onSave: (profile: Profile) => void;
  onCancel: () => void;
  isSaving?: boolean;
  customErrorMap?: MapCustomErrorFromRequest;
};

const FieldRow = ({ left, right }: { left: ReactNode; right: ReactNode }) => (
  <Box
    css={{
      display: 'flex',
      flexDirection: { base: 'column', lg: 'row' },
      gap: { base: 'looser', lg: 'default' },
      width: '100%',
      marginBlockEnd: 'looser',
    }}
  >
    <Box css={{ flex: 1, minWidth: 0 }}>{left}</Box>
    <Box css={{ flex: 1, minWidth: 0 }}>{right}</Box>
  </Box>
);

const TextField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  error,
  type = 'text',
  required = false,
  readOnly = false,
  autoComplete,
}: {
  label: string;
  name: string;
  value: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  error?: string | null;
  type?: string;
  required?: boolean;
  readOnly?: boolean;
  autoComplete?: string;
}) => (
  <FormField
    label={<Label data-testid={`${name}-label`}>{label}</Label>}
    control={
      <Input
        data-testid={`${name}-input`}
        type={type}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={onBlur}
        error={Boolean(error)}
        readOnly={readOnly}
        autoComplete={autoComplete}
        fluid
      />
    }
    description={
      error ? (
        <FormHelp data-testid={`${name}-error`} error>
          {error}
        </FormHelp>
      ) : undefined
    }
    error={Boolean(error)}
    required={required}
  />
);

export const EditRenterProfileContent = ({
  profile,
  setCustomErrorMap = () => {},
  onSave,
  onCancel,
  isSaving = false,
  customErrorMap = {
    firstName: undefined,
    lastName: undefined,
    phoneNumber: undefined,
  },
}: EditRenterProfileContentProps) => {
  const [form, setForm] = useState<Profile>(() => ({
    ...profile,
    // Format the stored phone (bare digits) so it displays as xxx-xxx-xxxx on mount, matching typing.
    phoneNumber: profile.phoneNumber ? formatPhoneNumberInput(profile.phoneNumber) : profile.phoneNumber,
  }));
  const [showErrors, setShowErrors] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const formFieldsRef = useRef<HTMLDivElement>(null);

  const update = (patch: Partial<Profile>) => {
    if (patch['firstName']) {
      setCustomErrorMap((prev) => ({ ...prev, firstName: undefined }));
    }
    if (patch['lastName']) {
      setCustomErrorMap((prev) => ({ ...prev, lastName: undefined }));
    }
    if (patch['phoneNumber']) {
      setCustomErrorMap((prev) => ({ ...prev, phoneNumber: undefined }));
    }

    setForm((prev) => ({ ...prev, ...patch }));
  };
  const markTouched = (name: string) => setTouched((prev) => ({ ...prev, [name]: true }));

  const fieldErrors = {
    firstName: validateName(form.firstName, 'First name') || customErrorMap?.firstName,
    lastName: validateName(form.lastName, 'Last name') || customErrorMap?.lastName,
    phoneNumber: validatePhone(form.phoneNumber) || customErrorMap?.phoneNumber,
    preferredMoveInDate: validateMoveInDate(form.preferredMoveInDate),
  };
  const hasErrors = Object.values(fieldErrors).some(Boolean);

  const visibleError = (name: keyof MapCustomErrorFromRequest) =>
    (touched[name] || showErrors ? fieldErrors[name] : null) ?? customErrorMap?.[name];

  const monthlyIncome =
    form.estimatedHouseholdIncome !== undefined ? form.estimatedHouseholdIncome / MONTHS_PER_YEAR : null;

  const handleSave = () => {
    if (hasErrors) {
      setShowErrors(true);
      formFieldsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    emitRenterProfileSubmitEvent(form);
    onSave(form);
  };

  const handleClearClick = () => emitRenterProfileClearEvent(form);

  return (
    <Box
      data-testid="edit-renter-profile-content"
      css={{
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        backgroundColor: 'bg.neutral',
        borderRadius: 'obj.default',
        paddingBlock: 'loose',
        paddingInline: 'loose',
      }}
    >
      <Box
        css={{
          gridColumn: { base: 'span 12', lg: 'span 8' },
          display: 'flex',
          flexDirection: 'column',
          gap: 'loose',
        }}
      >
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tightest' }}>
          <Heading level={2} textStyle="heading-xs">
            We&rsquo;ll include any details you share here with rental managers you contact.
          </Heading>
          <Text textStyle="body" css={{ color: 'text.subtle' }}>
            This helps complete the picture of you as a renter.
          </Text>
        </Box>

        <Form aria-label="Edit renter profile">
          <Box ref={formFieldsRef} css={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <FieldRow
              left={
                <TextField
                  label="First name"
                  name="first-name"
                  value={form.firstName ?? ''}
                  onChange={(val) => update({ firstName: val })}
                  onBlur={() => markTouched('firstName')}
                  error={visibleError('firstName')}
                  required={true}
                  autoComplete="given-name"
                />
              }
              right={
                <TextField
                  label="Last name"
                  name="last-name"
                  value={form.lastName ?? ''}
                  onChange={(val) => update({ lastName: val })}
                  onBlur={() => markTouched('lastName')}
                  error={visibleError('lastName')}
                  required={true}
                  autoComplete="family-name"
                />
              }
            />
            <FieldRow
              left={
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email ?? ''}
                  readOnly={true}
                  autoComplete="email"
                />
              }
              right={
                <TextField
                  label="Phone number"
                  name="phone-number"
                  type="tel"
                  value={form.phoneNumber ?? ''}
                  onChange={(val) => update({ phoneNumber: formatPhoneNumberInput(val) })}
                  onBlur={() => markTouched('phoneNumber')}
                  error={visibleError('phoneNumber')}
                  required={true}
                  autoComplete="tel"
                />
              }
            />
            <FieldRow
              left={
                <MoveInDateField
                  value={localDateToString(form.preferredMoveInDate)}
                  onChange={(val) => update({ preferredMoveInDate: stringToLocalDate(val) })}
                  error={showErrors ? fieldErrors.preferredMoveInDate : undefined}
                  portalRoot={document.getElementById(RENTER_PROFILE_ROOT_ID)}
                />
              }
              right={
                <ChipSelectField
                  label="Total people in household"
                  name="num-household"
                  options={NUM_HOUSEHOLD_OPTIONS}
                  value={form.totalPeopleInHousehold ?? null}
                  onChange={(val) =>
                    update({
                      totalPeopleInHousehold: (val as RenterProfileStandalonePageTotalPeopleInHousehold) ?? undefined,
                    })
                  }
                  onClear={handleClearClick}
                  labelInfo={
                    <Tooltip
                      content="Include yourself, other adults, and children who will live in the home."
                      portalRoot={document.getElementById(RENTER_PROFILE_ROOT_ID)}
                    />
                  }
                />
              }
            />

            <IncomeInputField
              value={form.estimatedHouseholdIncome ?? null}
              onChange={(val) => update({ estimatedHouseholdIncome: val ?? undefined })}
              portalRoot={document.getElementById(RENTER_PROFILE_ROOT_ID)}
            />

            <HousingVoucherField
              hasHousingVoucher={form.hasHousingVoucher ?? false}
              voucherAmountMonthly={form.voucherAmountMonthly ?? null}
              monthlyIncome={monthlyIncome}
              onHousingVoucherChange={(val) => update({ hasHousingVoucher: val })}
              onVoucherAmountChange={(val) => update({ voucherAmountMonthly: val ?? undefined })}
            />

            <ChipSelectField
              label="Pets"
              name="pets"
              options={PET_OPTIONS}
              value={form.pets ?? null}
              onChange={(val) => update({ pets: (val as RenterProfileHasPets) ?? undefined })}
              onClear={handleClearClick}
              bottomHelpText={
                <Text textStyle="body-xs" css={{ color: 'text.subtle' }}>
                  {ASSISTANCE_ANIMAL_DISCLAIMER_TEXT}{' '}
                  <Anchor href={HUD_ANIMAL_POLICY_URL} target="_blank" rel="noopener noreferrer">
                    Learn more
                  </Anchor>
                </Text>
              }
            />

            <CreditScoreField
              value={form.creditScore ?? null}
              onChange={(val) => update({ creditScore: val ?? undefined })}
              onNoCreditScoreSelect={() => emitRenterProfileNoCreditScoreEvent(form)}
              portalRoot={document.getElementById(RENTER_PROFILE_ROOT_ID)}
            />

            <ChipSelectField
              label="Preferred lease duration"
              name="lease-duration"
              options={LEASE_DURATION_OPTIONS}
              value={form.preferredLeaseDuration ?? null}
              onChange={(val) =>
                update({
                  preferredLeaseDuration: (val as RenterProfileStandalonePagePreferredLeaseDuration) ?? undefined,
                })
              }
              onClear={handleClearClick}
            />

            <ChipSelectField
              label="Preferred bedroom count"
              name="num-bedrooms"
              options={NUM_BEDROOMS_OPTIONS}
              value={form.preferredBedroomCount ?? null}
              onChange={(val) =>
                update({
                  preferredBedroomCount: (val as RenterProfileStandalonePagePreferredBedroomCount) ?? undefined,
                })
              }
              onClear={handleClearClick}
            />

            <ChipSelectField
              label="Parking needed"
              name="parking"
              options={PARKING_OPTIONS}
              value={form.isParkingNeeded === undefined ? null : form.isParkingNeeded ? 'YES' : 'NO'}
              onChange={(val) => update({ isParkingNeeded: val === null ? undefined : val === 'YES' })}
              onClear={handleClearClick}
            />
          </Box>
        </Form>

        <Box css={{ display: 'flex', gap: 'default', alignItems: 'center' }}>
          <Button
            data-testid="save-profile-button"
            emphasis="filled"
            onClick={handleSave}
            loading={isSaving}
            css={{ flex: 1 }}
          >
            Save
          </Button>
          <Button data-testid="cancel-profile-button" emphasis="bare" tone="brand" onClick={onCancel}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
